/**
 * ============================================================================
 * FORM VALIDATOR
 * ============================================================================
 *
 * Handles form validation and user feedback.
 *
 * ============================================================================
 */



class FormValidator {
  constructor() {
    this.forms = new Map();
    this.rules = new Map();

    this.init();
  }

  /**
   * Initialize form validator
   */
  init() {
    this.setupDefaultRules();
    this.bindEvents();
    this.initializeForms();

    console.log('✅ Form Validator initialized');
  }

  /**
   * Setup default validation rules
   */
  setupDefaultRules() {
    this.addRule('required', (value) => {
      return value.trim().length > 0;
    }, 'This field is required');

    this.addRule('email', (value) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    }, 'Please enter a valid email address');

    this.addRule('min', (value, param) => {
      return value.length >= parseInt(param);
    }, 'Must be at least {param} characters');

    this.addRule('max', (value, param) => {
      return value.length <= parseInt(param);
    }, 'Must be no more than {param} characters');

    this.addRule('pattern', (value, param) => {
      const regex = new RegExp(param);
      return regex.test(value);
    }, 'Please match the required format');

    this.addRule('match', (value, param, form) => {
      const matchField = form.querySelector(`[name="${param}"]`);
      return matchField ? value === matchField.value : false;
    }, 'Fields do not match');
  }

  /**
   * Bind events
   */
  bindEvents() {
    document.addEventListener('submit', (e) => {
      if (e.target.matches('[data-validate]')) {
        if (!this.validateForm(e.target)) {
          e.preventDefault();
        }
      }
    });

    document.addEventListener('blur', (e) => {
      if (e.target.matches('[data-rules]')) {
        this.validateField(e.target);
      }
    }, true);

    document.addEventListener('input', (e) => {
      if (e.target.matches('[data-rules]') && hasClass(e.target, 'is-invalid')) {
        this.validateField(e.target);
      }
    });
  }

  /**
   * Initialize forms with validation
   */
  initializeForms() {
    $$('[data-validate]').forEach(form => {
      this.initializeForm(form);
    });
  }

  /**
   * Initialize a single form
   * @param {Element} form - Form element
   */
  initializeForm(form) {
    const formId = form.id || `form-${Date.now()}`;
    if (!form.id) form.id = formId;

    this.forms.set(formId, {
      element: form,
      fields: new Map()
    });

    // Initialize fields
    $$('[data-rules]', form).forEach(field => {
      this.initializeField(field, formId);
    });
  }

  /**
   * Initialize a single field
   * @param {Element} field - Field element
   * @param {string} formId - Form ID
   */
  initializeField(field, formId) {
    const fieldName = field.name || field.id;
    const rules = data(field, 'rules').split('|');

    const formData = this.forms.get(formId);
    if (formData) {
      formData.fields.set(fieldName, {
        element: field,
        rules: rules
      });
    }
  }

  /**
   * Add validation rule
   * @param {string} name - Rule name
   * @param {Function} validator - Validation function
   * @param {string} message - Error message
   */
  addRule(name, validator, message) {
    this.rules.set(name, { validator, message });
  }

  /**
   * Validate entire form
   * @param {Element} form - Form element
   * @returns {boolean} True if valid
   */
  validateForm(form) {
    const formId = form.id;
    const formData = this.forms.get(formId);

    if (!formData) {
      this.initializeForm(form);
      return this.validateForm(form);
    }

    let isValid = true;

    formData.fields.forEach((fieldData) => {
      if (!this.validateField(fieldData.element)) {
        isValid = false;
      }
    });

    // Emit validation event
    document.dispatchEvent(new CustomEvent('form:validate', {
      detail: { form, isValid }
    }));

    return isValid;
  }

  /**
   * Validate single field
   * @param {Element} field - Field element
   * @returns {boolean} True if valid
   */
  validateField(field) {
    const rules = data(field, 'rules');
    if (!rules) return true;

    const value = field.value;
    const ruleList = rules.split('|');
    const form = field.closest('form');

    let isValid = true;
    let errorMessage = '';

    for (const ruleString of ruleList) {
      const [ruleName, param] = ruleString.split(':');
      const rule = this.rules.get(ruleName);

      if (rule) {
        const result = rule.validator(value, param, form);
        if (!result) {
          isValid = false;
          errorMessage = rule.message.replace('{param}', param);
          break;
        }
      }
    }

    this.updateFieldState(field, isValid, errorMessage);

    // Emit field validation event
    document.dispatchEvent(new CustomEvent('field:validate', {
      detail: { field, isValid, errorMessage }
    }));

    return isValid;
  }

  /**
   * Update field validation state
   * @param {Element} field - Field element
   * @param {boolean} isValid - Validation result
   * @param {string} errorMessage - Error message
   */
  updateFieldState(field, isValid, errorMessage) {
    const formGroup = field.closest('.form-group') || field.parentElement;

    if (isValid) {
      removeClass(field, 'is-invalid');
      addClass(field, 'is-valid');
      removeClass(formGroup, 'has-error');
    } else {
      removeClass(field, 'is-valid');
      addClass(field, 'is-invalid');
      addClass(formGroup, 'has-error');
    }

    // Update error message
    this.updateErrorMessage(field, errorMessage);
  }

  /**
   * Update error message for field
   * @param {Element} field - Field element
   * @param {string} message - Error message
   */
  updateErrorMessage(field, message) {
    const formGroup = field.closest('.form-group') || field.parentElement;
    let feedback = formGroup.querySelector('.form-feedback');

    if (!feedback) {
      feedback = document.createElement('div');
      feedback.className = 'form-feedback';
      formGroup.appendChild(feedback);
    }

    if (message) {
      feedback.textContent = message;
      addClass(feedback, 'is-invalid');
      removeClass(feedback, 'is-valid');
    } else {
      feedback.textContent = '';
      removeClass(feedback, 'is-invalid');
      addClass(feedback, 'is-valid');
    }
  }

  /**
   * Clear validation state for field
   * @param {Element} field - Field element
   */
  clearFieldState(field) {
    removeClass(field, 'is-valid');
    removeClass(field, 'is-invalid');

    const formGroup = field.closest('.form-group') || field.parentElement;
    removeClass(formGroup, 'has-error');

    const feedback = formGroup.querySelector('.form-feedback');
    if (feedback) {
      feedback.textContent = '';
      removeClass(feedback, 'is-valid');
      removeClass(feedback, 'is-invalid');
    }
  }

  /**
   * Clear validation state for entire form
   * @param {Element} form - Form element
   */
  clearFormState(form) {
    $$('[data-rules]', form).forEach(field => {
      this.clearFieldState(field);
    });
  }

  /**
   * Get form validation state
   * @param {Element} form - Form element
   * @returns {Object} Validation state
   */
  getFormState(form) {
    const fields = $$('[data-rules]', form);
    const state = {
      isValid: true,
      fields: {}
    };

    fields.forEach(field => {
      const isValid = !hasClass(field, 'is-invalid');
      state.fields[field.name || field.id] = isValid;

      if (!isValid) {
        state.isValid = false;
      }
    });

    return state;
  }
}

// Make FormValidator available globally
window.FormValidator = FormValidator;
